import React, { useState, useEffect } from 'react'
import { Youtube, Upload, Check, X, Globe, Lock, Eye, Loader2, ExternalLink, Unplug } from 'lucide-react'
import { youtubeApi, projectApi } from '../api/client'
import { ShortsMetadataPanel } from './ShortsMetadataPanel'
import { StyleProfileCard } from './StyleProfileCard'

interface YouTubePublishPanelProps {
  videoBlob: Blob | null
  downloadUrl?: string | null
  clipTitle: string
  originalTitle: string
  transcriptSnippet?: string
  transcriptText?: string
  clipStart?: number
  clipEnd?: number
  tagsHint?: string[]
  onClose: () => void
}

type Privacy = 'private' | 'unlisted' | 'public'

export function YouTubePublishPanel({
  videoBlob: videoBlobProp,
  downloadUrl,
  clipTitle,
  originalTitle,
  transcriptSnippet,
  transcriptText,
  clipStart = 0,
  clipEnd = 0,
  tagsHint,
  onClose,
}: YouTubePublishPanelProps) {
  // Resolved blob — either from prop or fetched from downloadUrl
  const [resolvedBlob, setResolvedBlob] = useState<Blob | null>(videoBlobProp)
  const [fetchingBlob, setFetchingBlob] = useState(false)

  // Keep resolvedBlob in sync with prop changes
  useEffect(() => {
    if (videoBlobProp) setResolvedBlob(videoBlobProp)
  }, [videoBlobProp])

  // Auto-fetch blob from download URL if no blob available
  useEffect(() => {
    if (resolvedBlob || !downloadUrl || fetchingBlob) return
    let cancelled = false
    setFetchingBlob(true)
    ;(async () => {
      try {
        const resp = await fetch(downloadUrl)
        if (!resp.ok || cancelled) return
        const blob = await resp.blob()
        if (!cancelled) setResolvedBlob(blob)
      } catch {
        // will show "export first" message
      } finally {
        if (!cancelled) setFetchingBlob(false)
      }
    })()
    return () => { cancelled = true }
  }, [downloadUrl, resolvedBlob, fetchingBlob])
  // Connection state
  const [isConnected, setIsConnected] = useState(false)
  const [isConfigured, setIsConfigured] = useState(true)
  const [channelEmail, setChannelEmail] = useState('')
  const [checkingConnection, setCheckingConnection] = useState(true)

  // Form state
  const [title, setTitle] = useState(clipTitle || 'Untitled Clip')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [privacy, setPrivacy] = useState<Privacy>('private')
  const [isShort, setIsShort] = useState(false)
  const [notifySubscribers, setNotifySubscribers] = useState(true)

  // Upload state
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadResult, setUploadResult] = useState<{ video_id: string; url: string } | null>(null)
  const [uploadError, setUploadError] = useState('')

  // Check YouTube connection on mount
  useEffect(() => {
    checkConnection()
  }, [])

  // Generate metadata when connected
  useEffect(() => {
    if (isConnected && !uploadResult) {
      generateMetadata()
    }
  }, [isConnected])

  const checkConnection = async () => {
    setCheckingConnection(true)
    try {
      const status = await youtubeApi.getStatus()
      setIsConfigured(status.configured)
      setIsConnected(status.connected)
      setChannelEmail(status.email || '')
    } catch {
      setIsConnected(false)
    } finally {
      setCheckingConnection(false)
    }
  }

  const generateMetadata = async () => {
    try {
      const meta = await youtubeApi.generateMetadata({
        clip_title: clipTitle,
        original_title: originalTitle,
        transcript_snippet: transcriptSnippet,
        tags_hint: tagsHint,
      })
      if (meta.success) {
        setTitle(meta.title)
        setDescription(meta.description)
        setTags(meta.tags)
      }
    } catch {
      // Use defaults — non-fatal
    }
  }

  const handleConnect = () => {
    // Redirect to YouTube OAuth — opens in same tab, will redirect back to /studio after
    window.location.href = youtubeApi.getConnectUrl()
  }

  const handleDisconnect = async () => {
    try {
      await youtubeApi.disconnect()
      setIsConnected(false)
      setChannelEmail('')
    } catch {
      // ignore
    }
  }

  const handleAddTag = () => {
    const t = tagInput.trim()
    if (t && !tags.includes(t)) {
      setTags(prev => [...prev, t])
    }
    setTagInput('')
  }

  const handleRemoveTag = (tag: string) => {
    setTags(prev => prev.filter(t => t !== tag))
  }

  const handlePublish = async () => {
    if (!resolvedBlob) {
      setUploadError('No video to upload. Export clips first.')
      return
    }
    if (!title.trim()) {
      setUploadError('Title is required.')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)
    setUploadError('')

    try {
      const result = await youtubeApi.publish(
        {
          videoBlob: resolvedBlob,
          title: title.trim(),
          description,
          tags,
          privacy,
          isShort,
          notifySubscribers,
        },
        (pct) => setUploadProgress(pct),
      )

      if (result.success) {
        setUploadResult({ video_id: result.video_id, url: result.url })
        // Record in export history
        projectApi.recordExport({
          filename: `${title.trim()}.mp4`,
          export_type: 'youtube',
          destination_url: result.url,
          metadata: { video_id: result.video_id, privacy },
        }).catch(() => {})
      } else {
        setUploadError('Upload failed. Please try again.')
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Upload failed'
      setUploadError(msg)
    } finally {
      setIsUploading(false)
    }
  }

  // Loading state
  if (checkingConnection) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-3 text-gray-400">
            <Loader2 size={20} className="animate-spin" />
            <span>Checking YouTube connection...</span>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (uploadResult) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="text-center">
            <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Published to YouTube!</h2>
            <p className="text-gray-400 text-sm mb-4">
              Your clip is now {privacy === 'public' ? 'live' : privacy} on YouTube.
            </p>
            <a
              href={uploadResult.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors mb-4"
            >
              <Youtube size={18} />
              <span>View on YouTube</span>
              <ExternalLink size={14} />
            </a>
            <br />
            <button
              onClick={onClose}
              className="mt-4 text-gray-400 hover:text-white text-sm transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-gray-800 rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Youtube size={22} className="text-red-500" />
            <h2 className="text-lg font-bold text-white">Publish to YouTube</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Not configured */}
          {!isConfigured && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-300">
              YouTube publishing is not configured on this server. Ask your admin to add
              GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET and enable the YouTube Data API v3.
            </div>
          )}

          {/* Not connected */}
          {isConfigured && !isConnected && (
            <div className="text-center py-6">
              <Youtube size={48} className="text-red-500 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">Connect Your YouTube Channel</h3>
              <p className="text-gray-400 text-sm mb-4">
                Sign in with Google to publish clips directly to your channel.
              </p>
              <button
                onClick={handleConnect}
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                <Youtube size={20} />
                Connect YouTube
              </button>
            </div>
          )}

          {/* Connected — show publish form */}
          {isConnected && (
            <>
              {/* Connection info */}
              <div className="flex items-center justify-between bg-gray-700/50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check size={16} className="text-green-400" />
                  <span className="text-gray-300">Connected as</span>
                  <span className="text-white font-medium">{channelEmail}</span>
                </div>
                <button
                  onClick={handleDisconnect}
                  className="text-gray-400 hover:text-red-400 text-xs flex items-center gap-1"
                  title="Disconnect YouTube"
                >
                  <Unplug size={14} />
                </button>
              </div>

              {/* My Style — analyze the connected channel into a voice profile */}
              <StyleProfileCard />

              {/* No video warning */}
              {!resolvedBlob && !fetchingBlob && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-300">
                  No exported clip available. Export your clips first, then publish.
                </div>
              )}
              {fetchingBlob && (
                <div className="flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3 text-sm text-indigo-300">
                  <Loader2 size={16} className="animate-spin" />
                  Preparing exported clip for upload…
                </div>
              )}

              {/* Smart Metadata Panel (AI-powered) */}
              <ShortsMetadataPanel
                clipTitle={clipTitle}
                originalTitle={originalTitle}
                transcriptText={transcriptText || transcriptSnippet || ''}
                clipStart={clipStart}
                clipEnd={clipEnd}
                onMetadataReady={(meta) => {
                  setTitle(meta.title)
                  setDescription(meta.description)
                  setTags(meta.tags)
                }}
                initialTitle={title}
                initialDescription={description}
                initialTags={tags}
              />

              {/* Privacy */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Visibility</label>
                <div className="flex gap-2">
                  {([
                    { value: 'private' as Privacy, icon: Lock, label: 'Private' },
                    { value: 'unlisted' as Privacy, icon: Eye, label: 'Unlisted' },
                    { value: 'public' as Privacy, icon: Globe, label: 'Public' },
                  ]).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setPrivacy(opt.value)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                        privacy === opt.value
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
                      }`}
                    >
                      <opt.icon size={14} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isShort}
                    onChange={e => setIsShort(e.target.checked)}
                    className="rounded bg-gray-700 border-gray-600 text-indigo-500 focus:ring-indigo-500"
                  />
                  YouTube Short
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifySubscribers}
                    onChange={e => setNotifySubscribers(e.target.checked)}
                    className="rounded bg-gray-700 border-gray-600 text-indigo-500 focus:ring-indigo-500"
                  />
                  Notify subscribers
                </label>
              </div>

              {/* Error */}
              {uploadError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
                  {uploadError}
                </div>
              )}

              {/* Upload progress */}
              {isUploading && (
                <div>
                  <div className="flex items-center justify-between text-sm text-gray-300 mb-1">
                    <span>Uploading to YouTube...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Publish button */}
              <button
                onClick={handlePublish}
                disabled={isUploading || !resolvedBlob || !title.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isUploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Publish to YouTube
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
